import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  createPlatformTenantAdmin,
  deletePlatformTenant,
  getPlatformTenant,
  getPlatformTenantAudit,
  impersonatePlatformTenantAdmin,
  patchPlatformTenant,
  patchPlatformTenantAdmin,
  type PlatformAuditEntry,
  type PlatformTenantDetail,
  type PlatformTenantAdminRow,
} from '@/api/platformApi'
import { setTenantSlug } from '@/config/tenant'
import { getPlatformTenantsPath } from '@/config/platform'
import { TenantPortalLinks } from '@/components/Platform/TenantPortalLinks'
import { PlatformTenantStoreAppSection } from '@/pages/Platform/PlatformTenantStoreAppSection'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import { setAdmin } from '@/utils/adminAuth'
import styles from './Platform.module.css'

function formatDateTime(iso: string | null | undefined) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('pt-PT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return String(iso)
  }
}

function formatQuota(used: number, limit: number | null | undefined) {
  if (limit == null) return `${used} (sem limite)`
  const pct = limit > 0 ? Math.round((used / limit) * 100) : 0
  return `${used} / ${limit} (${pct}%)`
}

function tenantInitials(nome: string, slug: string) {
  const base = (nome || slug || '?').trim()
  const parts = base.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return base.slice(0, 2).toUpperCase()
}

function healthLevel(dias: number, ultimo: string | null | undefined): 'ok' | 'warn' | 'critical' | 'none' {
  if (!ultimo) return 'none'
  if (dias >= 60) return 'critical'
  if (dias >= 30) return 'warn'
  return 'ok'
}

const HEALTH_CLASS: Record<string, string> = {
  ok: styles.tenantDetailHealthOk,
  warn: styles.tenantDetailHealthWarn,
  critical: styles.tenantDetailHealthCritical,
  none: styles.tenantDetailHealthNone,
}

const USAGE_KPIS = [
  { key: 'usuarios_total', label: 'Utilizadores', icon: '◉' },
  { key: 'admins_ativos', label: 'Admins activos', icon: '◎' },
  { key: 'profissionais_total', label: 'Profissionais', icon: '◈' },
  { key: 'unidades_total', label: 'Unidades', icon: '▣' },
  { key: 'agendamentos_total', label: 'Agendamentos', icon: '◷' },
  { key: 'agendamentos_mes_atual', label: 'Este mês', icon: '↗' },
] as const

function AdminCard({
  admin,
  detalhe,
  adminBusy,
  resetSenhaId,
  resetSenhaVal,
  onImpersonate,
  onToggleReset,
  onResetPassword,
  onToggleAdmin,
  onResetValChange,
}: {
  admin: PlatformTenantAdminRow
  detalhe: PlatformTenantDetail
  adminBusy: number | null
  resetSenhaId: number | null
  resetSenhaVal: string
  onImpersonate: (id: number) => void
  onToggleReset: (id: number) => void
  onResetPassword: (id: number) => void
  onToggleAdmin: (a: PlatformTenantAdminRow) => void
  onResetValChange: (v: string) => void
}) {
  return (
    <article className={`${styles.adminCard} ${!admin.ativo ? styles.adminCardOff : ''}`}>
      <div className={styles.adminCardMain}>
        <span className={styles.adminCardAvatar} aria-hidden>
          {(admin.nome || admin.email).slice(0, 1).toUpperCase()}
        </span>
        <div className={styles.adminCardBody}>
          <code className={styles.adminCardEmail}>{admin.email}</code>
          <p className={styles.adminCardName}>{admin.nome ?? '—'}</p>
        </div>
        {admin.ativo ? (
          <span className={styles.badgePillOn}>Activo</span>
        ) : (
          <span className={styles.badgePillOff}>Inactivo</span>
        )}
      </div>
      <div className={styles.adminCardActions}>
        <button
          type="button"
          className={styles.tenantActionBtn}
          disabled={!detalhe.ativo || adminBusy !== null || !admin.ativo}
          onClick={() => onImpersonate(admin.id)}
        >
          Entrar
        </button>
        <button
          type="button"
          className={styles.tenantActionBtn}
          disabled={adminBusy === admin.id}
          onClick={() => onToggleReset(admin.id)}
        >
          Repor senha
        </button>
        <button
          type="button"
          className={`${styles.tenantActionBtn} ${admin.ativo ? styles.tenantActionDanger : styles.tenantActionPrimary}`}
          disabled={adminBusy === admin.id}
          onClick={() => onToggleAdmin(admin)}
        >
          {admin.ativo ? 'Desactivar' : 'Activar'}
        </button>
      </div>
      {resetSenhaId === admin.id && (
        <div className={styles.adminCardReset}>
          <label className={styles.platLabel} htmlFor={`reset-senha-${admin.id}`}>
            Nova senha
          </label>
          <div className={styles.inlineResetRow}>
            <input
              id={`reset-senha-${admin.id}`}
              type="password"
              className={styles.platInput}
              placeholder="Mínimo 6 caracteres"
              value={resetSenhaVal}
              onChange={(e) => onResetValChange(e.target.value)}
              minLength={6}
            />
            <button type="button" className={styles.btnGradient} onClick={() => onResetPassword(admin.id)}>
              Guardar
            </button>
          </div>
        </div>
      )}
    </article>
  )
}

function auditActionLabel(action: string) {
  const map: Record<string, string> = {
    tenant_create: 'Conta criada',
    tenant_update: 'Conta actualizada',
    tenant_suspend: 'Conta suspensa',
    tenant_activate: 'Conta reactivada',
    tenant_delete: 'Conta apagada',
    admin_create: 'Admin criado',
    admin_update: 'Admin actualizado',
    admin_password_reset: 'Senha admin reposta',
    admin_activate: 'Admin activado',
    admin_deactivate: 'Admin desactivado',
    impersonate_admin: 'Impersonação (suporte)',
    tenant_store_app_update: 'App nas lojas actualizada',
    tenant_store_app_build_start: 'Build nativo iniciado',
  }
  return map[action] ?? action
}

type TenantEditForm = {
  slug: string
  nome: string
  plano: string
  limite_usuarios: string
  limite_agendamentos_mes: string
}

function formFromDetail(d: PlatformTenantDetail): TenantEditForm {
  return {
    slug: d.slug ?? '',
    nome: d.nome ?? '',
    plano: d.plano ?? '',
    limite_usuarios: d.limite_usuarios != null ? String(d.limite_usuarios) : '',
    limite_agendamentos_mes:
      d.limite_agendamentos_mes != null ? String(d.limite_agendamentos_mes) : '',
  }
}

function parseOptionalLimit(raw: string, label: string): number | null {
  const t = raw.trim()
  if (!t) return null
  const n = Number(t)
  if (!Number.isInteger(n) || n < 0) throw new Error(`${label} deve ser um número inteiro ≥ 0 ou vazio.`)
  return n
}

export function PlatformTenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const id = Number(tenantId)
  const [detalhe, setDetalhe] = useState<PlatformTenantDetail | null>(null)
  const [audit, setAudit] = useState<PlatformAuditEntry[]>([])
  const [erro, setErro] = useState('')
  const [loading, setLoading] = useState(true)
  const [acaoErro, setAcaoErro] = useState('')
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<TenantEditForm>({
    slug: '',
    nome: '',
    plano: '',
    limite_usuarios: '',
    limite_agendamentos_mes: '',
  })
  const [newAdmin, setNewAdmin] = useState({ email: '', senha: '', nome: '' })
  const [adminBusy, setAdminBusy] = useState<number | null>(null)
  const [resetSenhaId, setResetSenhaId] = useState<number | null>(null)
  const [resetSenhaVal, setResetSenhaVal] = useState('')
  const [deleteSlug, setDeleteSlug] = useState('')
  const [deleting, setDeleting] = useState(false)
  const { notifySaved, notifyError, notifyValidation } = useSaveFeedback()

  const load = useCallback(async () => {
    if (!Number.isInteger(id) || id < 1) {
      setErro('Identificador da conta inválido.')
      setLoading(false)
      return
    }
    setErro('')
    setLoading(true)
    try {
      const [d, auditRes] = await Promise.all([
        getPlatformTenant(id),
        getPlatformTenantAudit(id, 40),
      ])
      setDetalhe({ ...d, admins: Array.isArray(d.admins) ? d.admins : [] })
      setAudit(auditRes.items)
      setForm(formFromDetail(d))
    } catch (e) {
      setDetalhe(null)
      const msg = e instanceof Error ? e.message : 'Erro ao carregar.'
      setErro(msg)
      notifyError(e, msg)
    } finally {
      setLoading(false)
    }
  }, [id, notifyError])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (searchParams.get('edit') === '1' && detalhe && !loading) {
      setEditing(true)
    }
  }, [searchParams, detalhe, loading])

  const healthLabel = useMemo(() => {
    if (!detalhe) return ''
    const dias = detalhe.dias_sem_actividade ?? 0
    if (!detalhe.ultimo_agendamento_em) return `Sem agendamentos · conta criada há ${dias} dias`
    if (dias >= 60) return `Inactiva há ${dias} dias — follow-up comercial`
    if (dias >= 30) return `${dias} dias sem novo agendamento`
    return `Activa · último agendamento há ${dias} dia(s)`
  }, [detalhe])

  const startEditing = () => {
    if (detalhe) setForm(formFromDetail(detalhe))
    setAcaoErro('')
    setEditing(true)
    setSearchParams({ edit: '1' }, { replace: true })
  }

  const cancelEditing = () => {
    if (detalhe) setForm(formFromDetail(detalhe))
    setAcaoErro('')
    setEditing(false)
    setSearchParams({}, { replace: true })
  }

  const toggleAtivo = async () => {
    if (!detalhe) return
    setAcaoErro('')
    try {
      await patchPlatformTenant(detalhe.id, { ativo: !detalhe.ativo })
      await load()
      notifySaved(detalhe.ativo ? 'Conta desactivada.' : 'Conta activada.')
    } catch (e) {
      notifyError(e, 'Erro ao actualizar.')
      setAcaoErro(e instanceof Error ? e.message : 'Erro ao actualizar.')
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!detalhe) return
    setAcaoErro('')
    const slugTrim = form.slug.trim()
    const nomeTrim = form.nome.trim()
    if (!slugTrim) {
      notifyValidation('Indique o slug da conta.')
      return
    }
    if (!nomeTrim) {
      notifyValidation('Indique o nome público do negócio.')
      return
    }
    let limite_usuarios: number | null
    let limite_agendamentos_mes: number | null
    try {
      limite_usuarios = parseOptionalLimit(form.limite_usuarios, 'Limite de utilizadores')
      limite_agendamentos_mes = parseOptionalLimit(
        form.limite_agendamentos_mes,
        'Limite de agendamentos por mês'
      )
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Valor inválido.'
      notifyValidation(msg)
      setAcaoErro(msg)
      return
    }
    setSaving(true)
    try {
      await patchPlatformTenant(detalhe.id, {
        slug: slugTrim,
        nome: nomeTrim,
        plano: form.plano.trim() || null,
        limite_usuarios,
        limite_agendamentos_mes,
      })
      await load()
      setEditing(false)
      setSearchParams({}, { replace: true })
      notifySaved('Informação da conta actualizada.')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao guardar.'
      setAcaoErro(msg)
      notifyError(err, msg)
    } finally {
      setSaving(false)
    }
  }

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!detalhe) return
    setAcaoErro('')
    if (!newAdmin.email.trim() || newAdmin.senha.length < 6) {
      notifyValidation('E-mail e senha (≥6) são obrigatórios.')
      return
    }
    setAdminBusy(-1)
    try {
      await createPlatformTenantAdmin(detalhe.id, {
        email: newAdmin.email.trim(),
        senha: newAdmin.senha,
        nome: newAdmin.nome.trim() || undefined,
      })
      setNewAdmin({ email: '', senha: '', nome: '' })
      await load()
      notifySaved('Administrador criado.')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao criar admin.'
      setAcaoErro(msg)
      notifyError(err, msg)
    } finally {
      setAdminBusy(null)
    }
  }

  const handleToggleAdmin = async (admin: PlatformTenantAdminRow) => {
    if (!detalhe) return
    setAdminBusy(admin.id)
    try {
      await patchPlatformTenantAdmin(detalhe.id, admin.id, { ativo: !admin.ativo })
      await load()
      notifySaved(admin.ativo ? 'Admin desactivado.' : 'Admin activado.')
    } catch (err) {
      notifyError(err, 'Erro ao actualizar admin.')
    } finally {
      setAdminBusy(null)
    }
  }

  const handleResetPassword = async (adminId: number) => {
    if (!detalhe || resetSenhaVal.length < 6) {
      notifyValidation('Senha deve ter pelo menos 6 caracteres.')
      return
    }
    setAdminBusy(adminId)
    try {
      await patchPlatformTenantAdmin(detalhe.id, adminId, { senha: resetSenhaVal })
      setResetSenhaId(null)
      setResetSenhaVal('')
      await load()
      notifySaved('Senha reposta.')
    } catch (err) {
      notifyError(err, 'Erro ao repor senha.')
    } finally {
      setAdminBusy(null)
    }
  }

  const handleImpersonate = async (adminId?: number) => {
    if (!detalhe) return
    setAdminBusy(adminId ?? -2)
    try {
      const res = await impersonatePlatformTenantAdmin(detalhe.id, adminId)
      setTenantSlug(res.tenantSlug)
      setAdmin(null, res.email, {
        tenantSlug: res.tenantSlug,
        tenantNome: res.tenantNome,
      })
      const tenantQ = encodeURIComponent(res.tenantSlug)
      window.open(`/admin/dashboard?tenant=${tenantQ}`, '_blank', 'noopener,noreferrer')
      notifySaved(`Sessão admin aberta como ${res.email}.`)
    } catch (err) {
      notifyError(err, 'Erro ao impersonar.')
    } finally {
      setAdminBusy(null)
    }
  }

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!detalhe) return
    if (deleteSlug.trim().toLowerCase() !== detalhe.slug.trim().toLowerCase()) {
      notifyValidation('Digite o slug exacto da conta para confirmar.')
      return
    }
    if (!window.confirm(`Apagar permanentemente «${detalhe.nome}» (${detalhe.slug})? Esta acção não pode ser desfeita.`)) {
      return
    }
    setDeleting(true)
    try {
      await deletePlatformTenant(detalhe.id, deleteSlug.trim())
      notifySaved('Conta apagada.')
      navigate(getPlatformTenantsPath(), { replace: true })
    } catch (err) {
      notifyError(err, 'Erro ao apagar conta.')
      setAcaoErro(err instanceof Error ? err.message : 'Erro ao apagar.')
    } finally {
      setDeleting(false)
    }
  }

  if (!Number.isInteger(id) || id < 1) {
    return (
      <div className={styles.platPage}>
        <p className={styles.erro} role="alert">
          ID inválido.
        </p>
        <Link className={styles.panelLink} to={getPlatformTenantsPath()}>
          ← Voltar à lista
        </Link>
      </div>
    )
  }

  return (
    <div className={styles.platPage}>
      {loading ? (
        <div className={styles.tenantDetailLoading}>
          <div className={styles.tenantListSkeletonRow} />
          <div className={styles.tenantListSkeletonRow} />
        </div>
      ) : erro ? (
        <>
          <p className={styles.erro} role="alert">
            {erro}
          </p>
          <Link className={styles.panelLink} to={getPlatformTenantsPath()}>
            Voltar à lista
          </Link>
        </>
      ) : detalhe ? (
        <>
          <section className={styles.tenantDetailHero}>
            <div className={styles.tenantDetailHeroGlow} aria-hidden />
            <Link className={styles.tenantDetailBack} to={getPlatformTenantsPath()}>
              ← Todas as contas
            </Link>
            <div className={styles.tenantDetailHeroRow}>
              <span className={styles.tenantDetailAvatar} aria-hidden>
                {tenantInitials(detalhe.nome, detalhe.slug)}
              </span>
              <div className={styles.tenantDetailHeroText}>
                <div className={styles.tenantDetailTitleRow}>
                  <h1 className={styles.tenantDetailTitle}>{detalhe.nome || 'Sem nome'}</h1>
                  {detalhe.ativo ? (
                    <span className={styles.badgePillOn}>Activa</span>
                  ) : (
                    <span className={styles.badgePillOff}>Suspensa</span>
                  )}
                  {(detalhe.quota_usuarios_excedida || detalhe.quota_agendamentos_mes_excedida) && (
                    <span className={styles.badgeWarn}>Quota</span>
                  )}
                </div>
                <p className={styles.tenantDetailMeta}>
                  <code className={styles.tenantListSlug}>{detalhe.slug}</code>
                  <span>·</span>
                  <span>ID #{detalhe.id}</span>
                  {detalhe.plano?.trim() && (
                    <>
                      <span>·</span>
                      <span>{detalhe.plano}</span>
                    </>
                  )}
                </p>
                <p
                  className={`${styles.tenantDetailHealth} ${HEALTH_CLASS[healthLevel(detalhe.dias_sem_actividade ?? 0, detalhe.ultimo_agendamento_em)]}`}
                >
                  {healthLabel}
                </p>
              </div>
            </div>
            <div className={styles.tenantDetailHeroActions}>
              {!editing && (
                <button type="button" className={styles.btnGradient} onClick={startEditing}>
                  Editar informação
                </button>
              )}
              <button type="button" className={styles.btnOutline} onClick={() => load()}>
                Actualizar
              </button>
              <button type="button" className={styles.tenantActionBtn} onClick={toggleAtivo}>
                {detalhe.ativo ? 'Suspender' : 'Reactivar'}
              </button>
              <button
                type="button"
                className={`${styles.tenantActionBtn} ${styles.tenantActionPrimary}`}
                onClick={() => handleImpersonate()}
                disabled={!detalhe.ativo || adminBusy !== null}
              >
                Entrar como admin
              </button>
            </div>
          </section>

          {acaoErro && (
            <p className={styles.erro} role="alert">
              {acaoErro}
            </p>
          )}

          <section className={styles.detailPortalSection}>
            <header className={styles.detailSectionHead}>
              <span className={styles.detailSectionIcon} aria-hidden>
                ◇
              </span>
              <div>
                <h2 className={styles.detailSectionTitle}>Link da app</h2>
                <p className={styles.detailSectionSub}>Enviar ao cliente para aceder ao negócio</p>
              </div>
            </header>
            <TenantPortalLinks slug={detalhe.slug} nome={detalhe.nome} compact />
          </section>

          <PlatformTenantStoreAppSection
            tenantId={detalhe.id}
            tenantSlug={detalhe.slug}
            tenantNome={detalhe.nome}
            storeApp={detalhe.store_app}
            onSaved={(storeApp) => setDetalhe((d) => (d ? { ...d, store_app: storeApp } : d))}
          />

          <section className={styles.detailCard} aria-labelledby="det-dados">
            <h2 id="det-dados" className={styles.detailCardTitle}>
              Dados da conta
            </h2>
            {editing ? (
              <form className={styles.detailEditForm} onSubmit={handleSave}>
                <p className={styles.detailLead}>
                  Alterar o <strong>slug</strong> afecta o link <code>/e/slug</code> — avise o cliente se mudar.
                </p>
                <div className={styles.platField}>
                  <label className={styles.platLabel} htmlFor="ed-slug">
                    Slug
                  </label>
                  <input
                    id="ed-slug"
                    className={styles.platInput}
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    required
                  />
                </div>
                <div className={styles.platField}>
                  <label className={styles.platLabel} htmlFor="ed-nome">
                    Nome público
                  </label>
                  <input
                    id="ed-nome"
                    className={styles.platInput}
                    value={form.nome}
                    onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                    required
                  />
                </div>
                <div className={styles.platField}>
                  <label className={styles.platLabel} htmlFor="ed-plano">
                    Plano
                  </label>
                  <select
                    id="ed-plano"
                    className={styles.platInput}
                    value={
                      form.plano === 'gratis' || form.plano === 'completo' ? form.plano : form.plano
                    }
                    onChange={(e) => setForm((f) => ({ ...f, plano: e.target.value }))}
                  >
                    <option value="">Legado (acesso completo)</option>
                    <option value="gratis">Grátis</option>
                    <option value="completo">Completo — 9,70€/mês</option>
                    {form.plano && form.plano !== 'gratis' && form.plano !== 'completo' ? (
                      <option value={form.plano}>{form.plano}</option>
                    ) : null}
                  </select>
                  {detalhe.proxima_cobranca ? (
                    <p className={styles.platHint}>
                      Próxima cobrança (dia 1): {String(detalhe.proxima_cobranca).slice(0, 10)}. Quem
                      adere a meio do mês só paga no dia 1 seguinte.
                    </p>
                  ) : null}
                </div>
                <div className={styles.detailEditGrid}>
                  <div className={styles.platField}>
                    <label className={styles.platLabel} htmlFor="ed-lim-users">
                      Limite utilizadores
                    </label>
                    <input
                      id="ed-lim-users"
                      type="number"
                      min={0}
                      className={styles.platInput}
                      value={form.limite_usuarios}
                      onChange={(e) => setForm((f) => ({ ...f, limite_usuarios: e.target.value }))}
                      placeholder="Vazio = sem limite"
                    />
                  </div>
                  <div className={styles.platField}>
                    <label className={styles.platLabel} htmlFor="ed-lim-ag">
                      Limite agendamentos / mês
                    </label>
                    <input
                      id="ed-lim-ag"
                      type="number"
                      min={0}
                      className={styles.platInput}
                      value={form.limite_agendamentos_mes}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, limite_agendamentos_mes: e.target.value }))
                      }
                      placeholder="Vazio = sem limite"
                    />
                  </div>
                </div>
                <div className={styles.detailEditActions}>
                  <button type="button" className={styles.btnOutline} onClick={cancelEditing} disabled={saving}>
                    Cancelar
                  </button>
                  <button type="submit" className={styles.btnGradient} disabled={saving}>
                    {saving ? 'A guardar…' : 'Guardar alterações'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className={styles.detailInfoGrid}>
                  <div className={styles.detailInfoItem}>
                    <span className={styles.detailInfoLabel}>Plano</span>
                    <span className={styles.detailInfoValue}>{detalhe.plano?.trim() ? detalhe.plano : '—'}</span>
                  </div>
                  <div className={`${styles.detailInfoItem} ${detalhe.quota_usuarios_excedida ? styles.detailInfoWarn : ''}`}>
                    <span className={styles.detailInfoLabel}>Utilizadores</span>
                    <span className={styles.detailInfoValue}>
                      {formatQuota(detalhe.usuarios_total, detalhe.limite_usuarios)}
                    </span>
                  </div>
                  <div className={`${styles.detailInfoItem} ${detalhe.quota_agendamentos_mes_excedida ? styles.detailInfoWarn : ''}`}>
                    <span className={styles.detailInfoLabel}>Agend. / mês</span>
                    <span className={styles.detailInfoValue}>
                      {formatQuota(detalhe.agendamentos_mes_atual ?? 0, detalhe.limite_agendamentos_mes)}
                    </span>
                  </div>
                  <div className={styles.detailInfoItem}>
                    <span className={styles.detailInfoLabel}>Criada em</span>
                    <span className={styles.detailInfoValue}>{formatDateTime(detalhe.criado_em)}</span>
                  </div>
                  <div className={styles.detailInfoItem}>
                    <span className={styles.detailInfoLabel}>Último agendamento</span>
                    <span className={styles.detailInfoValue}>{formatDateTime(detalhe.ultimo_agendamento_em)}</span>
                  </div>
                  <div className={styles.detailInfoItem}>
                    <span className={styles.detailInfoLabel}>Última actualização</span>
                    <span className={styles.detailInfoValue}>{formatDateTime(detalhe.atualizado_em)}</span>
                  </div>
                </div>
                <p className={styles.detailEditHint}>
                  <button type="button" className={styles.linkGhost} onClick={startEditing}>
                    Editar estes campos
                  </button>
                </p>
              </>
            )}
          </section>

          <section className={styles.detailCard} aria-labelledby="det-uso">
            <h2 id="det-uso" className={styles.detailCardTitle}>
              Uso na base de dados
            </h2>
            <ul className={styles.detailKpiGrid}>
              {USAGE_KPIS.map((kpi) => {
                const raw = detalhe[kpi.key as keyof PlatformTenantDetail]
                const value = typeof raw === 'number' ? raw : Number(raw ?? 0)
                return (
                  <li key={kpi.key} className={styles.detailKpiCard}>
                    <span className={styles.detailKpiIcon} aria-hidden>
                      {kpi.icon}
                    </span>
                    <strong className={styles.detailKpiValue}>{value.toLocaleString('pt-PT')}</strong>
                    <span className={styles.detailKpiLabel}>{kpi.label}</span>
                  </li>
                )
              })}
            </ul>
          </section>

          <section className={styles.detailCard} aria-labelledby="det-admins">
            <h2 id="det-admins" className={styles.detailCardTitle}>
              Administradores
            </h2>
            {detalhe.admins.length === 0 ? (
              <p className={styles.panelEmpty}>Nenhum administrador.</p>
            ) : (
              <div className={styles.adminCardStack}>
                {detalhe.admins.map((a) => (
                  <AdminCard
                    key={a.id}
                    admin={a}
                    detalhe={detalhe}
                    adminBusy={adminBusy}
                    resetSenhaId={resetSenhaId}
                    resetSenhaVal={resetSenhaVal}
                    onImpersonate={handleImpersonate}
                    onToggleReset={(id) => {
                      setResetSenhaId(resetSenhaId === id ? null : id)
                      setResetSenhaVal('')
                    }}
                    onResetPassword={handleResetPassword}
                    onToggleAdmin={handleToggleAdmin}
                    onResetValChange={setResetSenhaVal}
                  />
                ))}
              </div>
            )}

            <form className={styles.adminCreateForm} onSubmit={handleCreateAdmin}>
              <h3 className={styles.platFormSectionTitle}>Adicionar administrador</h3>
              <div className={styles.detailEditGrid}>
                <div className={styles.platField}>
                  <label className={styles.platLabel} htmlFor="new-admin-email">
                    E-mail
                  </label>
                  <input
                    id="new-admin-email"
                    type="email"
                    className={styles.platInput}
                    placeholder="gestor@cliente.pt"
                    value={newAdmin.email}
                    onChange={(e) => setNewAdmin((f) => ({ ...f, email: e.target.value }))}
                    required
                  />
                </div>
                <div className={styles.platField}>
                  <label className={styles.platLabel} htmlFor="new-admin-senha">
                    Senha inicial
                  </label>
                  <input
                    id="new-admin-senha"
                    type="password"
                    className={styles.platInput}
                    placeholder="Mínimo 6 caracteres"
                    value={newAdmin.senha}
                    onChange={(e) => setNewAdmin((f) => ({ ...f, senha: e.target.value }))}
                    minLength={6}
                    required
                  />
                </div>
                <div className={`${styles.platField} ${styles.platFieldFull}`}>
                  <label className={styles.platLabel} htmlFor="new-admin-nome">
                    Nome (opcional)
                  </label>
                  <input
                    id="new-admin-nome"
                    className={styles.platInput}
                    placeholder="Ex.: Maria — recepção"
                    value={newAdmin.nome}
                    onChange={(e) => setNewAdmin((f) => ({ ...f, nome: e.target.value }))}
                  />
                </div>
              </div>
              <button type="submit" className={styles.btnOutline} disabled={adminBusy === -1}>
                {adminBusy === -1 ? 'A criar…' : 'Criar admin'}
              </button>
            </form>
          </section>

          <section className={styles.detailCard} aria-labelledby="det-audit">
            <h2 id="det-audit" className={styles.detailCardTitle}>
              Histórico de operações
            </h2>
            {audit.length === 0 ? (
              <p className={styles.panelEmpty}>Sem registos ainda.</p>
            ) : (
              <ul className={styles.auditTimeline}>
                {audit.map((entry) => (
                  <li key={entry.id} className={styles.auditTimelineItem}>
                    <span className={styles.auditTimelineDot} aria-hidden />
                    <div className={styles.auditTimelineBody}>
                      <span className={styles.auditAction}>{auditActionLabel(entry.action)}</span>
                      <time className={styles.auditTime}>{formatDateTime(entry.criado_em)}</time>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className={`${styles.detailCard} ${styles.dangerCard}`} aria-labelledby="det-danger">
            <h2 id="det-danger" className={styles.detailCardTitle}>
              Zona perigosa
            </h2>
            <p className={styles.detailLead}>
              Apagar a conta remove <strong>todos</strong> os dados (utilizadores, agendamentos, conteúdo). Irreversível.
            </p>
            <form className={styles.deleteForm} onSubmit={handleDelete}>
              <div className={styles.platField}>
                <label className={styles.platLabel} htmlFor="del-slug">
                  Digite <code>{detalhe.slug}</code> para confirmar
                </label>
                <input
                  id="del-slug"
                  className={styles.platInput}
                  value={deleteSlug}
                  onChange={(e) => setDeleteSlug(e.target.value)}
                  autoComplete="off"
                  placeholder={detalhe.slug}
                />
              </div>
              <button type="submit" className={styles.btnDanger} disabled={deleting}>
                {deleting ? 'A apagar…' : 'Apagar conta permanentemente'}
              </button>
            </form>
          </section>

          <p className={styles.detailFoot}>
            <button type="button" className={styles.linkGhost} onClick={() => navigate(getPlatformTenantsPath())}>
              ← Voltar à lista de contas
            </button>
          </p>
        </>
      ) : null}
    </div>
  )
}
